// AI_GOOD: Animation components extracted for better organization
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { SmoothFadeInTextProps, defaultMarkdownProps, animationStyles } from "@/pages/MissingArticle/types";
import { Loader2 } from "lucide-react";

// AI_GOOD: Line-by-line fade in animation component preserved exactly as original
export const SmoothFadeInText: React.FC<SmoothFadeInTextProps> = ({ 
  text,
  isParagraph = false,
  batchSize = 2, // Number of lines to animate together
  staggerDelay = 0.15, // Delay between batches
  allowMarkdown = false, // Whether to parse markdown
  markdownProps = defaultMarkdownProps, // Use default markdown props if not provided
}) => {
  // AI_GOOD: Split text into lines if it's a paragraph, otherwise treat as a single line
  const lines = isParagraph ? text.split('\n').filter(line => line.trim()) : [text];
  
  // AI_GOOD: Group lines into batches
  const batches: string[][] = [];
  for (let i = 0; i < lines.length; i += batchSize) {
    batches.push(lines.slice(i, i + batchSize));
  }
  
  return (
    <>
      {batches.map((batch, batchIndex) => (
        <motion.div
          key={`batch-${batchIndex}`}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            type: "spring",
            duration: 0.8,
            bounce: 0.1,
            delay: batchIndex * staggerDelay, // Staggered delay based on batch index
          }}
          className="mb-3"
        >
          {batch.map((line, lineIndex) => (
            <div key={`line-${batchIndex}-${lineIndex}`} className="mb-1">
              {allowMarkdown ? (
                <ReactMarkdown {...markdownProps}>{line}</ReactMarkdown>
              ) : (
                line
              )}
            </div>
          ))}
        </motion.div>
      ))}
    </>
  );
};

// AI_GOOD: Custom hook for animating streaming thoughts with incremental processing
export const useStreamingThoughts = (streamingThoughts: string) => {
  const [animatedThoughts, setAnimatedThoughts] = useState<string[]>([]);
  const processedLengthRef = useRef<number>(0);
  
  useEffect(() => {
    // AI_GOOD: Reset when content is cleared (new generation starts)
    if (!streamingThoughts || streamingThoughts.trim() === "") {
      setAnimatedThoughts([]);
      processedLengthRef.current = 0;
      return;
    }

    // AI_GOOD: Process only the NEW content since last update
    const newContent = streamingThoughts.slice(processedLengthRef.current);
    
    if (newContent.length > 0) {
      // AI_GOOD: Extract new thoughts from the delta content
      const newThoughts = newContent
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      // AI_GOOD: Add only genuinely new thoughts to avoid duplicates
      if (newThoughts.length > 0) {
        setAnimatedThoughts(prev => [...prev, ...newThoughts]);
      }
      
      // AI_GOOD: Update processed length to track progress
      processedLengthRef.current = streamingThoughts.length;
    }
  }, [streamingThoughts]);
  
  return animatedThoughts;
};

// AI_GOOD: Helper function to extract content sections from incremental chunks
const extractIncrementalSections = (newContent: string, existingSections: string[]): string[] => {
  if (!newContent.trim()) return [];
  
  // AI_GOOD: Look for natural breaking points in the new content
  // We'll be more conservative and only create new sections when we have clear boundaries
  const potentialSections: string[] = [];
  
  // AI_GOOD: Split on clear paragraph boundaries (double newlines)
  const paragraphChunks = newContent.split(/\n\s*\n/);
  
  for (let i = 0; i < paragraphChunks.length; i++) {
    const chunk = paragraphChunks[i].trim();
    
    if (chunk.length > 0) {
      // AI_GOOD: For the first chunk, check if it might be continuing the last section
      if (i === 0 && existingSections.length > 0) {
        // If this looks like a continuation (doesn't start with header/bullet), 
        // append to last section instead of creating new one
        const lastSection = existingSections[existingSections.length - 1];
        const startsWithMarkdown = /^(#{1,6}\s|[-*+]\s|\d+\.\s|\*\*)/;
        
        if (!startsWithMarkdown.test(chunk) && lastSection && !lastSection.includes('\n\n')) {
          // This is likely a continuation, we'll handle it by updating the last section
          // But since we can't modify existing sections, we'll create a combined section
          // and let the parent handle the merge logic
          potentialSections.push(chunk);
        } else {
          potentialSections.push(chunk);
        }
      } else {
        potentialSections.push(chunk);
      }
    }
  }
  
  return potentialSections;
};

// AI_GOOD: Custom hook for animating streaming content with incremental processing
export const useStreamingContent = (streamingContent: string) => {
  const [animatedContentSections, setAnimatedContentSections] = useState<string[]>([]);
  const processedLengthRef = useRef<number>(0);
  const pendingContentRef = useRef<string>("");
  const lastContentLengthRef = useRef<number>(0);
  
  useEffect(() => {
    // AI_GOOD: Reset when content is cleared (new generation starts)
    if (!streamingContent || streamingContent.trim() === "") {
      setAnimatedContentSections([]);
      processedLengthRef.current = 0;
      pendingContentRef.current = "";
      lastContentLengthRef.current = 0;
      return;
    }

    // AI_GOOD: Process only the NEW content since last update
    const newContent = streamingContent.slice(processedLengthRef.current);
    
    if (newContent.length > 0) {
      // AI_GOOD: Combine with any pending content from previous incomplete sections
      const contentToProcess = pendingContentRef.current + newContent;
      
      // AI_GOOD: Extract new sections from the delta content
      const newSections = extractIncrementalSections(contentToProcess, animatedContentSections);
      
      if (newSections.length > 0) {
        // AI_GOOD: Check if we have a complete paragraph boundary
        const hasCompleteParagraph = contentToProcess.includes('\n\n');
        
        if (hasCompleteParagraph) {
          // AI_GOOD: We have complete sections, add them
          const completeSections = newSections.slice(0, -1); // All but the last (potentially incomplete)
          const lastSection = newSections[newSections.length - 1];
          
          if (completeSections.length > 0) {
            setAnimatedContentSections(prev => [...prev, ...completeSections]);
          }
          
          // AI_GOOD: Keep the last section as pending if it might be incomplete
          const endsWithParagraphBreak = contentToProcess.trim().endsWith('\n\n');
          pendingContentRef.current = endsWithParagraphBreak ? "" : lastSection;
        } else {
          // AI_GOOD: No complete paragraphs yet, keep as pending
          pendingContentRef.current = contentToProcess;
        }
      }
      
      // AI_GOOD: Update processed length to track progress
      processedLengthRef.current = streamingContent.length;
    }
    
    // AI_GOOD: Track content length to detect when streaming stops
    lastContentLengthRef.current = streamingContent.length;
  }, [streamingContent]);
  
  // AI_GOOD: Flush pending content when streaming appears to have stopped
  useEffect(() => {
    if (streamingContent && streamingContent.length > 0) {
      // AI_GOOD: Use a timeout to detect when streaming has stopped
      const timeoutId = setTimeout(() => {
        if (pendingContentRef.current.trim() && 
            streamingContent.length === lastContentLengthRef.current) {
          // AI_GOOD: Streaming appears to have stopped, flush pending content
          setAnimatedContentSections(prev => [...prev, pendingContentRef.current.trim()]);
          pendingContentRef.current = "";
        }
      }, 1000); // Wait 1 second after last content update
      
      return () => clearTimeout(timeoutId);
    }
  }, [streamingContent]);
  
  return animatedContentSections;
};

// AI_GOOD: Streaming content display component with section-based animation
export interface StreamingContentDisplayProps {
  streamingContent: string;
  isStreamingToArticleArea: boolean;
  markdownProps?: any;
  currentThinkingSection?: string | null;
}

export const StreamingContentDisplay: React.FC<StreamingContentDisplayProps> = ({
  streamingContent,
  markdownProps = defaultMarkdownProps,
  currentThinkingSection,
}) => {
  // AI_GOOD: Use the streaming content animation hook
  const animatedContentSections = useStreamingContent(streamingContent);

  return (
    <div className="min-h-[200px] flex flex-col justify-center">
      {streamingContent ? (
        <div className="streaming-content-container prose max-w-none">
          {animatedContentSections.map((section, index) => (
            <motion.div
              key={`content-section-${section.substring(0, 30)}-${index}`} // Use content-based key for stability
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: "spring",
                duration: 0.8,
                bounce: 0.1,
                delay: index * 0.1, // Staggered delay for each new section
              }}
              className="mb-4"
            >
              <div className="animated-content-section prose prose-sm prose-h2:text-xl prose-h1:font-bold prose-h1:text-2xl max-w-none">
                <ReactMarkdown {...markdownProps}>{section}</ReactMarkdown>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="flex items-center justify-center p-6">
          <div className="text-center">
            {currentThinkingSection ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="text-transparent"
              >
                <span className="animate-text-gradient text-lg font-semibold">
                  {currentThinkingSection}...
                </span>
              </motion.div>
            ) : (
              <motion.span
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="block text-base"
              >
                Generating article content...
              </motion.span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// AI_GOOD: Streaming thoughts display component with fixed conditional rendering
export interface StreamingThoughtsDisplayProps {
  streamingThoughts: string;
  currentDisplayDoc?: any;
  isStreamingToArticleArea: boolean;
  isGenerating: boolean;
  markdownProps?: any;
  currentThinkingSection?: string | null;
}

export const StreamingThoughtsDisplay: React.FC<StreamingThoughtsDisplayProps> = ({
  streamingThoughts,
  currentDisplayDoc,
  isStreamingToArticleArea,
  isGenerating,
  markdownProps = defaultMarkdownProps,
  currentThinkingSection,
}) => {
  // AI_GOOD: Use the streaming thoughts animation hook
  const animatedThoughts = useStreamingThoughts(streamingThoughts);

  return (
    <>
      {isStreamingToArticleArea ? (
        // AI_GOOD: Show streaming thoughts when actively streaming with animation
        streamingThoughts.trim() ? (
          <div className="streaming-thoughts-container">
            {animatedThoughts.map((thought, index) => (
              <motion.div
                key={`thought-${thought.substring(0, 20)}-${index}`} // Use content-based key for stability
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  type: "spring",
                  duration: 0.6,
                  bounce: 0.1,
                  delay: 0.05,
                }}
                className="mb-2"
              >
                <div className="animated-thought prose prose-sm" style={animationStyles.animatedThought}>
                  <ReactMarkdown {...markdownProps}>{thought}</ReactMarkdown>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-4">
            <div className="text-start">
                <motion.span
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="block text-md"
                >
                Thinking about things...
              </motion.span>
            </div>
          </div>
        )
      ) : currentDisplayDoc?.thoughts ? (
        // AI_GOOD: Show saved thoughts from the backend only when not streaming
        <div className="saved-thoughts-container">
          {currentDisplayDoc.thoughts.split("\n").map((thought: string, index: number) => {
            if (!thought.trim()) return null;
            return (
              <div key={`saved-thought-${index}`} className="max-w-none p-2">
                <ReactMarkdown {...markdownProps}>
                  {thought}
                </ReactMarkdown>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-gray-500 italic flex items-center justify-center py-4">
          {isGenerating ? (
            <div className="text-center">
              <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
              <div>Processing thoughts...</div>
              {currentThinkingSection && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mt-1 text-sm text-blue-600"
                >
                  🤔 {currentThinkingSection}
                </motion.div>
              )}
            </div>
          ) : (
            "No thoughts were recorded during generation."
          )}
        </div>
      )}
    </>
  );
}; 